import React, {
  useState, useEffect, useContext, useMemo,
} from 'react';
import API, { setIframeApiConfig } from './apiConfig';
import * as cognito from './cognito';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import { jwtDecode } from 'jwt-decode';

const {
  signUpUserWithEmail: signUpWithEmail,
  verifyCode,
  getSession,
  getAttributes,
  setAttribute,
  sendCode,
  forgotPassword,
  changePassword,
  refreshSession: refreshSessionWithoutHandling,
} = cognito;

const AuthStatus = {
  Loading: 'Loading',
  SignedIn: 'SignedIn',
  SignedOut: 'SignedOut',
  Iframe: 'Iframe',
};

const defaultState = {
  sessionInfo: {},
  authStatus: AuthStatus.Loading,
};

export const AuthContext = React.createContext(defaultState);

export const useAuth = () => useContext(AuthContext);

export function AuthIsSignedIn({ children }) {
  const { authStatus } = useContext(AuthContext);

  return authStatus === AuthStatus.SignedIn && children;
}

export function AuthIsNotSignedIn({ children }) {
  const { authStatus } = useContext(AuthContext);

  return authStatus === AuthStatus.SignedOut && children;
}

export function AuthIsIframe({ children }) {
  const { authStatus } = useContext(AuthContext);

  return authStatus === AuthStatus.Iframe && children;
}

function AuthProvider({ children }) {
  const [authStatus, setAuthStatus] = useState(AuthStatus.Loading);
  const [sessionInfo, setSessionInfo] = useState({});
  const [attrInfo, setAttrInfo] = useState([]);
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const handleSessionInfo = async (sess) => {
    try {
      const {
        idToken, accessToken, refreshToken, error,
      } = queryString.parse(location.search);
      if (error) {
        toast.error(`Error during SSO: ${error}`);
        throw new Error(error);
      } else if (idToken && accessToken) {
        cognito.setSignInUserSession(idToken, accessToken, refreshToken);
      }
      const session = sess || await getSession();
      setSessionInfo(session);
      window.localStorage.setItem('accessToken', session.accessToken.jwtToken);
      window.localStorage.setItem('refreshToken', session.refreshToken.token);
      window.localStorage.setItem('idToken', session.idToken.jwtToken);
      const attr = await getAttributes();
      setAttrInfo(attr);
      setAuthStatus(AuthStatus.SignedIn);
      return true;
    } catch (err) {
      console.log('handleSessionInfo:: err:', err);
      setAuthStatus(AuthStatus.SignedOut);
      throw err;
    }
  };

  const signInWithEmail = async (username, password) => {
    try {
      return handleSessionInfo(await cognito.signInWithEmail(username, password));
    } catch (err) {
      setAuthStatus(AuthStatus.SignedOut);
      if (err.code === 'NotAuthorizedException') {
        const r = await API.post('/wrongPassword', { username });
        if (!r.data.success && r.data.message === 'UserDisabled') {
          throw new Error('User disabled');
        }
      }
      throw err;
    }
  };

  const refreshSession = async () => {
    handleSessionInfo(await refreshSessionWithoutHandling(sessionInfo.refreshToken));
  };

  const handleLogoutAudit = async () => {
    try {
      await API.patch('v1/userlogs', {
        resource: 'mindlabs',
        action: 'signout',
      });
    } catch (err) {
      console.log('Error:: handleLoginAudit::', err.message, err);
    }
  };

  const signOut = async (kickOutToSignInPage = true) => {
    if (['Stelisbiopharma01', 'stelisbiopharma'].includes(window.localStorage.getItem('orgId'))) {
      await handleLogoutAudit();
    }
    cognito.signOut();
    window.localStorage.removeItem('orgId');
    window.localStorage.removeItem('accessToken');
    window.localStorage.removeItem('refreshToken');
    window.localStorage.removeItem('idToken');
    window.localStorage.removeItem('lastActiveAt');
    if (kickOutToSignInPage !== false && authStatus !== AuthStatus.Iframe) setAuthStatus(AuthStatus.SignedOut);
  };

  useEffect(() => {
    if (searchParams.get('iframeToken') && searchParams.get('iframeOrgId')) {
      const idToken = searchParams.get('iframeToken');
      setSessionInfo({ idToken: { jwtToken: idToken, payload: jwtDecode(idToken) } });
      setIframeApiConfig(idToken, searchParams.get('iframeOrgId'));
      setAuthStatus(AuthStatus.Iframe);
      return;
    }
    if (authStatus === 'Loading') handleSessionInfo().catch(() => { });
  }, [authStatus]);

  const state = useMemo(() => ({
    authStatus,
    sessionInfo,
    attrInfo,
    signUpWithEmail,
    signInWithEmail,
    signOut,
    verifyCode,
    getSession,
    sendCode,
    forgotPassword,
    changePassword,
    getAttributes,
    setAttribute,
    refreshSession,
  }), [
    authStatus,
    sessionInfo,
    attrInfo,
  ]);

  return (
    authStatus !== AuthStatus.Loading && (
      <AuthContext.Provider value={state}>
        {children}
      </AuthContext.Provider>
    )
  );
}

export default AuthProvider;
